import {
  assert_string_hash_sha256,
  assert_string_not_empty,
  assert_string_object_id,
  assert_string_object_id_not_empty,
  assert_type_array,
  assert_type_boolean,
  assert_type_enum,
} from '@shared/asserts/utils_asserts';
import { assert_string_uuid_not_empty } from '@shared/asserts/utils_asserts_string';
import { DTO__base } from '@shared/common/DTO__base';
import { ENUM__string_id_empty } from '@shared/constants/ENUM__string_id_empty';
import { ENUM__ml_asset_family } from '@shared/dto/+model/sd/_enums/ENUM__ml_asset_family';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { ENUM__field_kind } from '../../_enums/ENUM__field_kind';
import { ERROR_GRAPH__base } from '../../_errors/ERROR_GRAPH__base';
import { ENUM__field_role } from '../../_invocations/_base/ENUM__field_role';
import { ENUM__invocation_type } from '../../_invocations/_base/ENUM__invocation_type';
import { ENUM__field_graph_interaction } from './_enums/ENUM__field_graph_interaction';
import { ENUM__field_store_interaction } from './_enums/ENUM__field_store_interaction';

function shouldDebugHasComponent(instance: any, value_new: boolean): boolean {
  return instance.node_type === ENUM__invocation_type.SD__DENOISE_LATENTS_PIPELINE;
}

export class DTO_META__field_info extends DTO__base {
  /*
   * STATIC FIELDS
   */
  static _class_name_static: string = ENUM__dto_class_name.GRAPH_FIELD_META_INFO;

  /*
   * STATIC METHODS
   */

  /*
   * FIELDS
   */

  graph_id!: string;

  /*
   *
   */

  node_id!: string;
  node_type!: ENUM__invocation_type;
  node_family!: ENUM__ml_asset_family;

  /*
   *
   */

  field_id!: string;

  field_name!: string;
  field_path!: string;
  field_group!: string;

  field_role!: string;
  field_kind!: ENUM__field_kind;

  /*
   *
   */
  field_hash__value_full!: string; // field value + field path
  field_hash__value_clean!: string; //field value const + field path
  field_hash__value_core!: string; // field value

  field_hash__calculated!: boolean;
  field_hash__need_include_path!: boolean;

  /*
   *
   */

  grid2d__can_be_dimension!: boolean;
  grid2d__is_row!: boolean;
  grid2d__is_column!: boolean;

  /*
   *
   */
  generation__has_effect_static!: boolean;
  generation__dynamic_duration!: boolean;

  /*
   *
   */

  graph__interaction!: ENUM__field_graph_interaction;
  /*
   *
   */

  data__is_linked!: boolean;
  data__synced_by_parent_id!: string;
  data__is_required!: boolean;
  data__is_validated!: boolean;
  data__populate_from_linked!: boolean;
  data__reset_if_not_linked!: boolean;

  /*
   *
   */

  store__interaction!: ENUM__field_store_interaction;
  store__family!: ENUM__ml_asset_family;

  /*
   *
   */

  ui__is_visible!: boolean;
  ui__is_readonly!: boolean;
  ui__has_component!: boolean;

  /*
   *
   */
  errors!: ERROR_GRAPH__base[];

  /*
   * PROPERTIES
   */

  store__read_is_enabled() {
    return (
      this.store__interaction == ENUM__field_store_interaction.READ_AND_WRITE || this.store__interaction == ENUM__field_store_interaction.ONLY_READ_FROM_STORE
    );
  }

  store__write_is_enabled() {
    return (
      this.store__interaction == ENUM__field_store_interaction.READ_AND_WRITE || this.store__interaction == ENUM__field_store_interaction.ONLY_WRITE_TO_STORE
    );
  }

  get kind__is_output() {
    return this.field_kind == ENUM__field_kind.OUTPUT;
  }

  get kind__is_input() {
    return this.field_kind == ENUM__field_kind.INPUT;
  }

  get field_role_normalized() {
    let out = this.field_role.trim().replace(/\s+/g, ' ').toLowerCase();

    return out;
  }

  store__get_key() {
    let out = this.field_role_normalized;

    if (out == ENUM__field_role.UNKNOWN) out = `${this.node_type}.${this.field_path}`;

    return out;
  }
  store__get_family() {
    let out = this.store__family;

    if (out == ENUM__ml_asset_family.UNKNOWN) out = this.node_family;

    return out;
  }

  get field_path_full() {
    return `${this.node_type}.${this.field_path}`;
  }

  get field_path_full_human_readable() {
    let field_name = this.field_role.is_blank() ? this.field_path : this.field_role;

    let out = `${this.node_type}.${field_name}`;

    return out;
  }

  get sort_criteria() {
    return this.field_role.is_blank() ? `${this.node_type}.${this.field_path}` : this.field_role;
  }

  get grid2d__is_dimension() {
    return this.grid2d__is_row || this.grid2d__is_column;
  }

  get db_filter_to_find_image_meta() {
    const { graph_id, node_id, field_id } = this;

    assert_string_object_id_not_empty(graph_id);
    assert_string_uuid_not_empty(node_id);
    assert_string_uuid_not_empty(field_id);

    return { graph_id, node_id, field_id };
  }

  /*
   * METHODS
   */

  constructor() {
    super();

    this.errors_clear();

    this.generation__dynamic_duration = false;
    this.field_role = '';

    this.field_hash__value_full = ENUM__string_id_empty.HASH_SHA256;
    this.field_hash__value_clean = ENUM__string_id_empty.HASH_SHA256;
    this.field_hash__value_core = ENUM__string_id_empty.HASH_SHA256;

    this.data__is_linked = false;
    this.data__synced_by_parent_id = ENUM__string_id_empty.UUID;
    this.data__is_required = true;
    this.data__populate_from_linked = true;
    this.data__reset_if_not_linked = false;

    this.store__interaction = ENUM__field_store_interaction.ONLY_READ_FROM_STORE;
    this.store__family = ENUM__ml_asset_family.UNKNOWN;

    this.generation__has_effect_static = true;
  }

  errors_clear() {
    this.data__is_validated = false;
    this.errors = [];
  }

  id_create() {
    this.field_id = crypto.randomUUID();
  }

  copy_meta_from_parent(parent: DTO_META__field_info) {
    this.graph_id = parent.graph_id;

    [this.node_id, this.node_type, this.node_family] = [parent.node_id, parent.node_type, parent.node_family];

    this.field_group = parent.field_name;

    this.field_kind = parent.field_kind;
    this.ui__is_readonly = this.ui__is_readonly || parent.ui__is_readonly; //if parent is readonly - child is readonly too
  }

  assert_standard_fields(): void {
    assert_string_object_id(this.graph_id);
    assert_string_uuid_not_empty(this.node_id);
    assert_type_enum(this.node_family, ENUM__ml_asset_family);

    assert_string_uuid_not_empty(this.field_id);

    assert_string_not_empty(this.field_name);
    assert_string_not_empty(this.field_path);
    assert_string_hash_sha256(this.field_hash__value_full);
    assert_string_hash_sha256(this.field_hash__value_clean); // todo:implement
    assert_string_hash_sha256(this.field_hash__value_core);

    assert_type_boolean(this.generation__dynamic_duration);

    assert_type_boolean(this.ui__is_visible);
    assert_type_boolean(this.ui__has_component);
    assert_type_boolean(this.ui__is_readonly);

    assert_type_enum(this.graph__interaction, ENUM__field_graph_interaction);

    assert_type_enum(this.field_kind, ENUM__field_kind);

    assert_type_boolean(this.data__is_linked);
    assert_type_boolean(this.data__is_required);
    assert_type_boolean(this.data__populate_from_linked);

    assert_type_boolean(this.field_hash__calculated);
    assert_type_boolean(this.field_hash__need_include_path);

    assert_type_boolean(this.grid2d__can_be_dimension);
    assert_type_boolean(this.grid2d__is_row);
    assert_type_boolean(this.grid2d__is_column);

    assert_type_array(this.errors);

    assert_type_enum(this.store__interaction, ENUM__field_store_interaction);
    assert_type_enum(this.store__family, ENUM__ml_asset_family);
  }

  toString(): string {
    return `${this.node_type}.${this.field_path}`;
  }
}

// todo:review and mb use
export type FILTER_OF<T> = {
  [K in keyof T as T[K] extends Function ? never : K]?: T[K][];
};
